import {
    isChrome,
    isFF,
    isSafari,
    isIE,
    isEdge,
    isEdgeChromium,
    isMobile,
    isWin,
} from '@ringcentral/rwc-utils/src/client-detection';
import { isAutotestMode } from '@rwc/utils/func';

const DETECT_TIMEOUT = 500;
// 500ms is too short that timeout will often trigger before blur
const SAFARI_DETECT_TIMEOUT = 1500;
const PROMPT_ANSWER_TIMEOUT = 5000;
const CANCEL_CONFIRM_TIMEOUT = isWin ? 3000 : 1000;

const callback = cb => typeof cb === 'function' && cb();
const createHiddenIframe = () => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    return iframe;
};

const removeHiddenIframe = iframe => {
    document.body.removeChild(iframe);
};

const waitForPromptAnswer = (openCb, cancelCb) => {
    const finish = cb => {
        window.clearTimeout(timeout);
        window.removeEventListener('focus', focusHandler);
        window.removeEventListener('blur', blurHandler);
        document.removeEventListener('visibilitychange', visibilityHandler);
        callback(cb);
    };

    const focusHandler = () => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(
            () => finish(document.hasFocus() ? cancelCb : openCb),
            CANCEL_CONFIRM_TIMEOUT
        );
    };

    const blurHandler = () => finish(openCb);
    const visibilityHandler = () => document.visibilityState === 'hidden' && finish(openCb);
    let timeout = window.setTimeout(() => finish(openCb), PROMPT_ANSWER_TIMEOUT);

    window.addEventListener('focus', focusHandler);
    window.addEventListener('blur', blurHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
};

const watchLaunchBlur = (detectTimeout, successCb, noHandlerCb, cancelCb) => {
    const blurHandler = () => {
        window.clearTimeout(timeout);
        window.removeEventListener('blur', blurHandler);
        if (cancelCb) {
            waitForPromptAnswer(successCb, cancelCb);
        } else {
            callback(successCb);
        }
    };

    const timeout = window.setTimeout(() => {
        window.removeEventListener('blur', blurHandler);
        callback(noHandlerCb);
    }, detectTimeout);

    window.addEventListener('blur', blurHandler);
};

const defaultBlurHandler = (uri, successCb, noHandlerCb, cancelCb) => {
    // Blur hack (Chrome)
    watchLaunchBlur(DETECT_TIMEOUT, successCb, noHandlerCb, cancelCb);
    window.location.href = uri;
};

const iframeBlurHandler = (uri, detectTimeout, successCb, noHandlerCb, cancelCb) => {
    const runCheck = () => {
        const $iframe = createHiddenIframe();
        watchLaunchBlur(detectTimeout, successCb, noHandlerCb, cancelCb);
        $iframe.contentWindow.location.href = uri;
    };

    if (document.readyState === 'complete') {
        runCheck();
    } else {
        window.addEventListener('load', runCheck);
    }
};

/**
 * App launch in IE
 *
 * @param {String} uri - app launch scheme
 * @param {Function} successCb
 * @param {Function} noHandlerCb
 */
const ieChecker = (uri, successCb, noHandlerCb) => {
    openUriWithMsLaunchUri(uri, successCb, noHandlerCb);
    window.setTimeout(function () {
        callback(successCb);
    }, DETECT_TIMEOUT);
};

/**
 * App launch in Microsoft Edge old
 *
 * @param {String} uri - app launch scheme
 * @param {Function} successCb
 * @param {Function} noHandlerCb
 */
function openUriWithMsLaunchUri(uri, successCb, noHandlerCb) {
    navigator.msLaunchUri(uri, successCb, noHandlerCb);
}

// This module is full of different hacks to detect that application is installed
export const launchApplication = (uri, successCb, noHandlerCb, unknownCb, cancelCb) => {
    const _isAutotestMode = isAutotestMode();
    if (typeof _isAutotestMode !== 'undefined') {
        return _isAutotestMode ? callback(successCb) : callback(noHandlerCb);
    }

    if (isChrome() || isEdgeChromium()) {
        defaultBlurHandler(uri, successCb, noHandlerCb, cancelCb);
    } else if (isSafari()) {
        if (isMobile) {
            defaultBlurHandler(uri, successCb, noHandlerCb, cancelCb);
        } else {
            iframeBlurHandler(uri, SAFARI_DETECT_TIMEOUT, successCb, noHandlerCb, cancelCb);
        }
    } else if (isFF()) {
        iframeBlurHandler(uri, DETECT_TIMEOUT, successCb, noHandlerCb, cancelCb);
    } else if (isIE()) {
        ieChecker(uri, successCb, noHandlerCb);
    } else if (isEdge()) {
        openUriWithMsLaunchUri(uri, successCb, noHandlerCb);
    } else {
        // No hack we can use, just open the URL in an hidden iframe and invoke `unknownCallback`
        const iframe = createHiddenIframe();
        iframe.contentWindow.location.href = uri;
        window.setTimeout(function () {
            removeHiddenIframe(iframe);
            callback(unknownCb);
        }, DETECT_TIMEOUT);
    }
};
